import { Field } from '@headlessui/react';
import React, { FC, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { twJoin, twMerge } from 'tailwind-merge';

import MinusIcon from '../../../../../../assets/icons/minus-icon';
import { Checkbox } from '../../../../../../components/checkbox/index';
import Loader from '../../../../../../components/loader/index';
import { PaginationController } from '../../../../../../components/pagination-controller/index';
import { QueryKey } from '../../../../../../constants/index';
import { ScenarioHooks, useInvalidateQueries, usePagination } from '../../../../../../hooks/index';
import { useScenarioCapTableStore } from '../../../../cap-table.store';
import { useScenarioStore } from '../../../../scenario-modeling.store';
import { LiquidityFormSchema } from '../../../liquidity-validation';
import { TableHeaderItem } from '../form-components/index';
import { ClassConversionItem } from './class-conversion-item';
import { useClassConversionPendingStore } from './items.store';
import { ExistingItemListProps, itemsToFetchMap } from './items.types';

export const ClassConversionList: FC<ExistingItemListProps> = ({
  control,
  scenarioId,
  columns,
}) => {
  const { classConversion } = useWatch<LiquidityFormSchema>({ control });
  const [currentPage, setCurrentPage] = usePagination(`scenario-class-conversion:${scenarioId}`);
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[1]);
  const [initialSelectedIds, setInitialSelectedIds] = useState<string[]>([]);
  const { pointerBlockedStyles } = useScenarioStore();
  const { isPending } = useClassConversionPendingStore();

  const { scenarioConversionTasks, totalPages, isLoading } =
    ScenarioHooks.useScenarioClassConversionTasks({
      scenarioId,
      numberOfFetchedItems: Number(itemsToFetch),
      currentPage,
    });
  const { scheduleUpdate } = useScenarioCapTableStore();
  const { deleteScenarioClassConversions } = ScenarioHooks.useDeleteAllScenarioClassConversions();
  const { selectAllClassConversions } = ScenarioHooks.useSelectAllScenarioClassConversions();

  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_SCENARIO_CLASS_CONVERSIONS,
    QueryKey.GET_SCENARIO,
  );

  const onSelectAll = () => {
    scheduleUpdate(scenarioId);
    selectAllClassConversions(
      { scenarioId },
      {
        onSuccess: () => {
          invalidateQuery();
        },
      },
    );
  };
  const onRemoveSelectAll = () => {
    scheduleUpdate(scenarioId);

    deleteScenarioClassConversions(
      { scenarioId },
      {
        onSuccess: () => {
          invalidateQuery();
        },
      },
    );
  };

  useEffect(() => {
    const nextSelectedIds =
      classConversion?.classConversionItems?.map(
        ({ classConversionTaskId }) => classConversionTaskId || '',
      ) || [];
    setInitialSelectedIds((prev) => {
      if (
        prev.length === nextSelectedIds.length &&
        prev.every((id, index) => id === nextSelectedIds[index])
      ) {
        return prev;
      }
      return nextSelectedIds;
    });
  }, [classConversion?.classConversionItems]);

  const records = scenarioConversionTasks;
  return (
    <div
      className={twMerge(
        'relative flex w-full min-w-[1520px] flex-col items-end gap-3',
        isPending || isLoading ? 'pointer-events-none opacity-50' : '',
      )}
    >
      {(isPending || isLoading) && <Loader className="absolute z-10" />}
      {records && records.length > 0 && (
        <>
          <div
            className={twMerge(
              'flex w-full min-w-[1520px] flex-col rounded-lg border-[1px] border-gray-100 bg-white',
              'min-w-[1380px]',
            )}
          >
            <div
              className="grid"
              style={{
                gridTemplateColumns: `48px repeat(4, minmax(140px, 1fr)) 190px`,
              }}
            >
              <div
                className={twJoin(
                  'relative flex h-full w-full items-center justify-center border-b-[1px] border-b-gray-100 bg-gray-50 px-4',
                  pointerBlockedStyles,
                )}
              >
                <Field className="flex cursor-pointer items-center">
                  <Checkbox
                    checked={initialSelectedIds?.length > 0}
                    id="select-all"
                    onChange={(checked) => (checked ? onSelectAll() : onRemoveSelectAll())}
                    selectIcon={<MinusIcon />}
                  />
                </Field>
              </div>
              {columns?.map((column, index) => (
                <TableHeaderItem key={index} text={column} />
              ))}
            </div>
            {records.map((item) => (
              <div
                className="grid border-b-[1px] border-b-gray-100"
                key={item.id}
                style={{
                  gridTemplateColumns: `48px repeat(4, minmax(140px, 1fr)) 190px`,
                }}
              >
                <ClassConversionItem
                  control={control}
                  isChecked={!!initialSelectedIds.find((selectedId) => selectedId === item.id)}
                  item={item}
                  scenarioId={scenarioId}
                />
              </div>
            ))}
          </div>
        </>
      )}
      <PaginationController
        className="sticky right-0"
        currentPage={currentPage}
        itemsToFetch={itemsToFetch}
        itemsToFetchMap={itemsToFetchMap}
        onClick={setCurrentPage}
        onFetchAmountChange={setItemsToFetch}
        totalPages={totalPages || 1}
      />
    </div>
  );
};
